import { useState } from "react";
import { getCard, getCase, getOrganization } from "../../content/lookups.js";
import { getStage } from "../../content/stages.js";
import { useGame } from "../../app/GameProvider.js";
import { CardView } from "../../components/CardView.js";
import { exportClearImage } from "./exportClearImage.js";
import { ResultNav } from "./ResultNav.js";
import { useResultExit } from "./ResultExitContext.js";
import { FallingPieces } from "./FallingPieces.js";

export function ResultScreen() {
  const { state, dispatch } = useGame();
  const exit = useResultExit();
  const [isExporting, setIsExporting] = useState(false);
  const run = state.run;
  if (!run) return null;

  const win = state.screen === "result.clear" || run.outcome === "clear";
  const title = win ? "プロジェクト完了！" : "組織崩壊";
  const copy = win
    ? "最終プロジェクトを期限内に成功させた。"
    : "全社疲労が限界に達した。";
  const deck = [...run.deck, ...run.hand, ...run.discard, ...run.inPlay, ...run.aside];
  const displayDeck = [...deck].sort((a, b) => {
    const aDefinition = getCard(a.cardId);
    const bDefinition = getCard(b.cardId);
    const typeOrder = (type: string | undefined) => type === "worker" ? 0 : type === "action" ? 1 : 2;
    return typeOrder(aDefinition?.type) - typeOrder(bDefinition?.type)
      || (aDefinition?.name ?? a.cardId).localeCompare(bDefinition?.name ?? b.cardId, "ja")
      || a.instanceId.localeCompare(b.instanceId);
  });
  const stage = getStage(run.stageId);
  const finalCase = run.activeCase ? getCase(run.activeCase.caseId) : undefined;
  const saveClearImage = async () => {
    setIsExporting(true);
    try {
      await exportClearImage({
        stageName: stage.name,
        finalCaseName: finalCase?.name ?? "未完了",
        fatigue: run.fatigue,
        clearedNodes: run.clearedNodeIds.length,
        organizations: run.organizations.map((id) => getOrganization(id)?.name ?? id),
        deck: displayDeck,
      });
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <section className="screen screen-result">
      {win ? <FallingPieces /> : null}
      <p
        className="kicker"
        style={{ color: win ? "var(--progress)" : "var(--fatigue)", letterSpacing: "0.2em", fontSize: 11 }}
      >
        {win ? "CLEAR" : "GAME OVER"}
      </p>
      <h1 style={{ fontSize: 28, margin: "8px 0" }}>{title}</h1>
      <p style={{ color: "var(--muted)", lineHeight: 1.7 }}>{copy}</p>
      <div className={`result-report ${win ? "is-clear" : "is-gameover"}`}>
        <div className="result-report-heading">
          <span>RUN REPORT</span>
          <strong>{stage.name}</strong>
        </div>
        <dl className="result-stats">
          <div>
            <dt>最終プロジェクト</dt>
            <dd>{finalCase?.name ?? "未完了"}</dd>
          </div>
          <div>
            <dt>踏破</dt>
            <dd>{run.clearedNodeIds.length} マス</dd>
          </div>
          <div>
            <dt>最終疲労</dt>
            <dd className="is-fatigue">{run.fatigue}<small>/10</small></dd>
          </div>
        </dl>

        <section className="result-deck" aria-label="クリア時のデッキ">
          <div className="result-section-heading">
            <span>FINAL DECK</span>
            <strong>{deck.length} 枚</strong>
          </div>
          <ul className="result-deck-grid">
            {displayDeck.map((card) => {
              const definition = getCard(card.cardId);
              return (
                <li key={card.instanceId}>
                  {definition ? <CardView card={card} definition={definition} disabled className="result-deck-card" /> : null}
                </li>
              );
            })}
          </ul>
        </section>

        <section className="result-organizations" aria-label="組織基盤">
          <span>ORGANIZATIONS</span>
          <p>{run.organizations.map((id) => getOrganization(id)?.name ?? id).join(" ／ ") || "なし"}</p>
        </section>
      </div>
      {win ? (
        <button type="button" className="btn btn-gold" onClick={() => void saveClearImage()} disabled={isExporting}>
          {isExporting ? "画像を作成中…" : "クリア画像を保存"}
        </button>
      ) : null}
      <ResultNav stageId={run.stageId} dispatch={dispatch} exit={exit} />
    </section>
  );
}
